import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import moment from 'moment';
import { sendChatPost } from 'flow/actions/chat-actions';
import BaseModal from 'flow/components/ui-components/BaseModal';
import UserAvatar from 'flow/components/ui-components/UserAvatar';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import ImagePicker from 'flow/pages/messages/ImagePicker';
import { ChatSessionKey } from 'flow/types/EntityKeys';

import { extractName, getContactAvatar } from 'flow/utils';
import { replaceWhitespaces } from 'flow/utils/message-utils';

interface PostContent {
    attachment: File;
    title: string;
    message: string;
    imageSrc: string;
}

const defaultValue: PostContent = {
    attachment: null,
    title: '',
    message: '',
    imageSrc: '',
};

interface Props {
    id?: ChatSessionKey;
}

const CreatePostModal = ({ id }: Props) => {
    const { i18next } = useAppContext();
    const { ownExtension } = useExtension();
    const contact = ownExtension.get('contact');
    const dispatch = useAppDispatch();
    const { isSinglePaneMode } = useSinglePaneMode();

    const clientKey = useAppSelector((state) =>
        state.chats.getIn(['client', 'key']),
    );

    const avatarImg = getContactAvatar(contact);

    const [postContent, setPostContent] = useState<PostContent>(defaultValue);

    const handleSubmit = () => {
        dispatch(
            sendChatPost(
                id,
                clientKey,
                postContent.title,
                postContent.message,
                postContent.attachment
                    ? {
                          name: replaceWhitespaces(
                              postContent.attachment.name,
                              '_',
                          ),
                          file: postContent.attachment,
                      }
                    : null,
            ),
        );
    };

    const handleAttachment = (attachment: File) => {
        if (attachment) {
            const reader = new FileReader();
            reader.readAsDataURL(attachment);

            reader.onloadend = () => {
                setPostContent((prev) => ({
                    ...prev,
                    imageSrc: reader.result.toString(),
                    attachment,
                }));
            };
        }
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('chat.post.create')}
            onSubmit={handleSubmit}
            disableSubmit={!postContent.title || !postContent.message}
            confirmButtonText={i18next.t('Chat.Create.Button.Create')}
            className={!isSinglePaneMode && 'min-h-[796px] min-w-[688px]'}
        >
            <div className="flex flex-col gap-5">
                <ImagePicker
                    imageSrc={postContent.imageSrc}
                    removeAttachment={() => {
                        setPostContent((prev) => ({
                            ...prev,
                            imageSrc: '',
                            attachment: null,
                        }));
                    }}
                    addAttachment={(e) => {
                        handleAttachment(e.target?.files[0]);
                        e.target.value = null;
                    }}
                />

                <input
                    aria-label={i18next.t('Chat.Post.Create.ChatTitle')}
                    className="w-full bg-transparent text-2xl font-bold outline-hidden placeholder:text-gray-500"
                    placeholder={i18next.t('Chat.Post.Create.ChatTitle')}
                    value={postContent.title}
                    type="text"
                    onChange={(e) => {
                        const title = e.target.value;

                        setPostContent((prev) => ({
                            ...prev,
                            title,
                        }));
                    }}
                />

                <div className="flex justify-between">
                    <div className="flex items-center gap-x-2">
                        <UserAvatar src={avatarImg} size="small" />
                        <p className="line-clamp-1 text-sm font-bold">
                            {extractName(contact)}
                        </p>
                    </div>
                    <p className="text-xs text-gray-500">
                        {moment().format('HH:mm')}
                    </p>
                </div>

                <textarea
                    aria-label={i18next.t('input.writemessage')}
                    className="w-full resize-none bg-transparent outline-hidden placeholder:text-gray-500"
                    rows={10}
                    placeholder={i18next.t('input.writemessage')}
                    value={postContent.message}
                    onChange={(e) => {
                        const message = e.target.value;

                        setPostContent((prev) => ({
                            ...prev,
                            message,
                        }));
                    }}
                />
            </div>
        </BaseModal>
    );
};

export default CreatePostModal;
